package com.dotdrives.controller

import com.dotdrives.client.PublicGivingException
import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.*
import com.dotdrives.repository.PublicGivingRepository
import kotlin.math.roundToInt
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.*

data class PublicGivingUiState(
    val loading: Boolean = true,
    val isPreview: Boolean = false,
    val form: PublicGivingForm? = null,
    val amount: String = "",
    val frequency: String = "once",
    val name: String = "",
    val email: String = "",
    val fields: Map<String, JsonElement> = emptyMap(),
    val coverFees: Boolean = false,
    val anonymous: Boolean = false,
    val employer: String = "",
    val busy: Boolean = false,
    val attempt: PublicGivingAttempt? = null,
    val checkoutUrl: String? = null,
    val paymentStatus: String? = null,
    val reconciliationRequired: Boolean = false,
    val error: String? = null,
    val restorationFailed: Boolean = false,
) {
    val completed
        get() = paymentStatus == "completed"

    val locked
        get() = attempt != null || busy || restorationFailed

    val giftCents
        get() = givingCents(amount)

    val contributionCents
        get(): Int {
            attempt?.let {
                return it.contributionCents
            }
            val policy = form?.contributionPolicy ?: return 0
            return if (coverFees && policy.enabled)
                ((giftCents ?: 0).toLong() * policy.basisPoints / 10000.0).roundToInt() +
                    policy.fixedCents
            else 0
        }

    val totalCents
        get() = (giftCents ?: 0) + contributionCents

    val match
        get() = potentialEmployerMatch(email, employer, form?.config?.employerMatches.orEmpty())
}

class PublicGivingController(
    private val repo: PublicGivingRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val slug: String,
    private val newId: () -> String = ::uuidV7,
    private val saveAttempt: (PublicGivingAttempt?) -> Unit = {},
    private val onRevisionReady: () -> Unit = {},
    private val readAttempt: (() -> PublicGivingAttempt?)? = null,
    private val previewForm: PublicGivingForm? = null,
) : DotController<PublicGivingUiState>(initialGivingState(previewForm), scope) {
    companion object {
        /** Uses only the already authorized editor snapshot; no transport or browser storage. */
        fun preview(form: PublicGivingForm, scope: CoroutineScope): PublicGivingController =
            PublicGivingController(PreviewGivingRepository, scope, form.orgId, form.publicSlug, previewForm = form)
    }

    private val checkoutJson = Json { encodeDefaults = true }

    /** Only a successful read (including genuine absence) releases the restoration gate. */
    fun restore() {
        if (previewForm != null) return
        if (state.value.busy) return
        val read = readAttempt ?: return
        val saved = try {
            read()?.also {
                require(it.request.orgId == orgId && it.request.publicSlug == slug)
            }
        } catch (e: Throwable) {
            if (e is CancellationException) throw e
            update { it.copy(loading = false, restorationFailed = true, checkoutUrl = null,
                error = "We couldn’t restore your saved payment. Restore browser storage access and retry. If this continues, contact the organization before giving again.") }
            return
        }
        update { it.copy(restorationFailed = false) }
        load(saved)
    }

    fun load(restored: PublicGivingAttempt? = null) {
        if (previewForm != null) return
        if (state.value.restorationFailed) return
        val saved = restored?.takeIf { it.request.orgId == orgId && it.request.publicSlug == slug }
        val r = saved?.request
        update {
            it.copy(
                loading = true,
                error = null,
                attempt = saved,
                amount = r?.amount.orEmpty(),
                name = r?.donorName.orEmpty(),
                email = r?.donorEmail.orEmpty(),
                fields = r?.fields.orEmpty(),
                paymentStatus = if (saved?.statusToken != null) "pending" else null,
            )
        }
        scope.launch {
            try {
                val form = repo.form(orgId, slug)
                requireGivingInput(form.orgId == orgId && form.publicSlug == slug && form.currency == "usd") {
                    "This form is unavailable."
                }
                validateForm(form)
                val default =
                    form.config.amounts.firstOrNull { it.isDefault }
                        ?: form.config.amounts.firstOrNull()
                update {
                    it.copy(
                        loading = false,
                        form = form,
                        amount =
                            r?.amount
                                ?: default
                                    ?.amount
                                    ?.let { givingAmount((it * 100).roundToInt()) }
                                    .orEmpty(),
                        name = r?.donorName.orEmpty(),
                        email = r?.donorEmail.orEmpty(),
                        frequency = r?.frequency ?: "once",
                        fields = r?.fields.orEmpty(),
                        coverFees = r?.coverFees ?: false,
                        anonymous = r?.anonymous ?: false,
                        attempt = saved,
                        paymentStatus = if (saved?.statusToken != null) "pending" else null,
                    )
                }
            } catch (e: Exception) {
                fail(e, loading = false)
            }
        }
    }

    private fun edit(change: (PublicGivingUiState) -> PublicGivingUiState) {
        if (!state.value.locked) update { change(it).copy(error = null) }
    }

    fun setAmount(value: String) = edit { it.copy(amount = value) }

    fun setFrequency(value: String) = edit { it.copy(frequency = value) }

    fun setName(value: String) = edit { it.copy(name = value) }

    fun setEmail(value: String) = edit { it.copy(email = value) }

    fun setField(key: String, value: JsonElement) = edit {
        it.copy(fields = it.fields + (key to value))
    }

    fun setCoverFees(value: Boolean) = edit { it.copy(coverFees = value) }

    fun setAnonymous(value: Boolean) = edit { it.copy(anonymous = value) }

    fun setEmployer(value: String) = update { it.copy(employer = value) }

    fun submit() {
        if (previewForm != null) return
        val current = state.value
        val form = current.form ?: return
        if (current.restorationFailed || current.busy || current.completed) return
        if (!form.paymentsReady && current.attempt == null) {
            update {
                it.copy(
                    error =
                        "Online giving is not available yet. You can review this form and contact ${form.orgName} about other ways to give."
                )
            }
            return
        }
        val request =
            current.attempt?.request
                ?: try {
                    buildRequest(current, form)
                } catch (e: Exception) {
                    fail(e)
                    return
                }
        val attempt =
            current.attempt
                ?: PublicGivingAttempt(request, contributionCents = current.contributionCents)
        try {
            saveAttempt(attempt)
        } catch (e: Exception) {
            if (e is CancellationException) throw e
            fail(
                GivingValidationException(
                    "Allow session storage in this browser to safely continue to checkout."
                )
            )
            return
        }
        update { it.copy(busy = true, attempt = attempt, error = null, checkoutUrl = null) }
        scope.launch {
            try {
                val result = repo.checkout(request)
                val gift = givingCents(request.amount) ?: error("Invalid gift amount")
                val fee = attempt.contributionCents
                requireGivingInput(
                    result.operationId == request.operationId &&
                        result.currency == "usd" &&
                        result.giftCents == gift &&
                        result.contributionCents == fee &&
                        result.totalCents == gift + fee
                ) {
                    "The payment totals changed. Please contact the organization before continuing."
                }
                requireGivingInput(Regex("[a-f0-9]{64}").matches(result.statusToken)) {
                    "Payment status could not be saved. Please retry."
                }
                requireGivingInput(result.status in listOf("checkout_ready", "pending_confirmation")) {
                    "Payment status is unavailable."
                }
                requireGivingInput(result.url == null || safeCheckoutUrl(result.url)) {
                    "A secure checkout link was not returned. Please retry."
                }
                val saved = attempt.copy(statusToken = result.statusToken)
                saveAttempt(saved)
                update {
                    it.copy(
                        busy = false,
                        attempt = saved,
                        checkoutUrl = result.url,
                        paymentStatus = "pending",
                    )
                }
            } catch (e: Exception) {
                fail(e)
            }
        }
    }

    /** The server atomically retires only an unprepared operation, including delayed requests. */
    fun reviseGift() {
        if (previewForm != null) return
        val current = state.value
        val attempt = current.attempt ?: return
        if (current.restorationFailed || current.busy || attempt.statusToken != null) return
        update { it.copy(busy = true, error = null, checkoutUrl = null) }
        scope.launch {
            try {
                val result = repo.restartCheck(attempt.request)
                requireGivingInput(
                    result.operationId == attempt.request.operationId && result.restartAllowed
                ) {
                    "The original checkout may still be active. Retry the original gift or contact the organization; a new payment has not been started."
                }
                // Keep the saved lock if refresh or storage fails. Retirement is idempotent.
                val latest = repo.form(orgId, slug)
                validateForm(latest)
                saveAttempt(null)
                update {
                    PublicGivingUiState(loading = false, form = latest, amount = current.amount)
                }
                onRevisionReady()
            } catch (e: Exception) {
                fail(e)
            }
        }
    }

    private fun validateForm(form: PublicGivingForm) {
        requireGivingInput(form.orgId == orgId && form.publicSlug == slug && form.currency == "usd") {
            "This form is unavailable."
        }
        requireGivingInput(form.config.fields.all(::compatibleContactField)) {
            "This form needs an update. Please contact the organization about its contact fields or another way to give."
        }
    }

    fun checkStatus() {
        if (previewForm != null) return
        val current = state.value
        val attempt = current.attempt ?: return
        val token = attempt.statusToken ?: return
        if (current.restorationFailed || current.busy || current.completed) return
        update { it.copy(busy = true, error = null, checkoutUrl = null) }
        scope.launch {
            try {
                val result = repo.status(orgId, attempt.request.operationId, token)
                requireGivingInput(result.status in listOf("pending", "completed", "failed")) {
                    "Payment status is unavailable."
                }
                update {
                    it.copy(
                        busy = false,
                        paymentStatus = result.status,
                        reconciliationRequired = result.reconciliationRequired,
                    )
                }
            } catch (e: Exception) {
                fail(e)
            }
        }
    }

    /** Only a verified terminal status permits another operation; canceled redirects do not. */
    fun newGift() {
        if (previewForm != null) return
        if (state.value.restorationFailed) return
        if (
            state.value.paymentStatus !in listOf("completed", "failed") ||
                state.value.reconciliationRequired
        )
            return
        saveAttempt(null)
        update { PublicGivingUiState(loading = false, form = it.form, amount = it.amount) }
    }

    private fun buildRequest(
        state: PublicGivingUiState,
        form: PublicGivingForm,
    ): PublicCheckoutRequest {
        val cents =
            givingCents(state.amount)
                ?: throw GivingValidationException(
                    "Enter an amount from $0.50 to $1,000,000 with at most two decimal places."
                )
        requireGivingInput(
            form.config.toggles.customAmount ||
                form.config.amounts.any { (it.amount * 100).roundToInt() == cents }
        ) {
            "Choose one of the suggested amounts."
        }
        requireGivingInput(state.totalCents <= 100000000) { "The total must be no more than $1,000,000." }
        requireGivingInput(
            state.frequency == "once" ||
                (state.frequency == "monthly" && form.config.toggles.monthlyGiving) ||
                (state.frequency == "quarterly" && form.config.toggles.quarterlyGiving)
        ) {
            "Choose an available giving frequency."
        }
        requireGivingInput(
            state.email.length <= 254 &&
                Regex("\\S+@[^\\s@]+\\.[^\\s@]+").matches(state.email.trim())
        ) {
            "Enter a valid email address."
        }
        requireGivingInput(
            !state.coverFees || (form.config.toggles.coverFees && form.contributionPolicy.enabled)
        ) {
            "The optional contribution is unavailable."
        }
        requireGivingInput(!state.anonymous || form.config.toggles.anonymousGifts) {
            "Anonymous giving is unavailable."
        }
        val fields =
            state.fields
                .filterKeys { key -> form.config.fields.any { it.key == key } }
                .toMutableMap()
        form.config.fields.forEach { def ->
            when (def.key) {
                "email" -> fields[def.key] = JsonPrimitive(state.email.trim())
                "name",
                "full_name" -> fields[def.key] = JsonPrimitive(state.name.trim())
            }
            if (!def.required && fields[def.key]?.jsonPrimitive?.content?.isBlank() == true)
                fields.remove(def.key)
            val value = fields[def.key]?.jsonPrimitive
            requireGivingInput(
                !def.required ||
                    (value != null && value.content.isNotBlank() && value.content != "false")
            ) {
                "Complete ${def.key.replace('_', ' ')}."
            }
            if (value != null) {
                requireGivingInput(value.content.length <= 2000) { "${def.key} is too long." }
                requireGivingInput(def.type != "checkbox" || value.booleanOrNull != null) {
                    "Check ${def.key}."
                }
                requireGivingInput(
                    def.type != "email" || Regex("\\S+@[^\\s@]+\\.[^\\s@]+").matches(value.content)
                ) {
                    "Enter a valid ${def.key}."
                }
                requireGivingInput(
                    def.type != "number" || Regex("-?[0-9]+(\\.[0-9]+)?").matches(value.content)
                ) {
                    "Enter a number for ${def.key}."
                }
                requireGivingInput(def.options == null || value.content in def.options) {
                    "Choose an option for ${def.key}."
                }
            }
        }
        val donorName =
            if (form.config.fields.any { it.key in listOf("first_name", "last_name") }) {
                listOf("first_name", "last_name")
                    .mapNotNull {
                        fields[it]?.jsonPrimitive?.content?.trim()?.takeIf(String::isNotBlank)
                    }
                    .joinToString(" ")
            } else state.name.trim()
        requireGivingInput(donorName.isNotBlank() && donorName.length <= 200) { "Enter your name." }
        val request =
            PublicCheckoutRequest(
                orgId,
                slug,
                newId(),
                form.version,
                givingAmount(cents),
                state.frequency,
                donorName,
                state.email.trim(),
                fields,
                state.coverFees,
                state.anonymous,
            )
        val body = checkoutJson.encodeToString(request)
        requireGivingInput(body.encodeToByteArray().size <= 16 * 1024) {
            "Shorten your additional answers before continuing to checkout."
        }
        return request
    }

    private fun fail(error: Exception, loading: Boolean = state.value.loading) {
        if (error is CancellationException) throw error
        update {
            it.copy(
                loading = loading,
                busy = false,
                checkoutUrl = null,
                error = when (error) {
                    is PublicGivingException, is GivingValidationException -> error.message
                    else -> "We couldn’t complete the request. Please try again."
                },
            )
        }
    }
}

/** Only locally authored validation copy crosses the UI error boundary unchanged. */
private class GivingValidationException(message: String) : Exception(message)

private inline fun requireGivingInput(condition: Boolean, message: () -> String) {
    if (!condition) throw GivingValidationException(message())
}

private fun initialGivingState(preview: PublicGivingForm?): PublicGivingUiState {
    if (preview == null) return PublicGivingUiState()
    val amount = preview.config.amounts.firstOrNull { it.isDefault } ?: preview.config.amounts.firstOrNull()
    return PublicGivingUiState(
        loading = false,
        isPreview = true,
        form = preview.copy(paymentsReady = false),
        amount = amount?.amount?.let { givingAmount((it * 100).roundToInt()) }.orEmpty(),
    )
}

/** Defense in depth: preview controllers have no transport even if a future action misses its guard. */
private object PreviewGivingRepository : PublicGivingRepository {
    override suspend fun form(orgId: String, slug: String): PublicGivingForm = error("Preview has no giving service")
    override suspend fun checkout(request: PublicCheckoutRequest): PublicCheckoutResult = error("Preview payments are disabled")
    override suspend fun restartCheck(request: PublicCheckoutRequest): PublicRestartResult = error("Preview payments are disabled")
    override suspend fun status(orgId: String, operationId: String, token: String): PublicPaymentStatus = error("Preview payments are disabled")
}
